/**
 * SOLID Review
 * S — Strong. One concern: the cookie preferences — booting the CMP while its provider shows, opening the preferences (the certified
 *     CMP first, the first-party modal as the fallback), the modal's open state, saving the choice.
 * O — Good. A new way in is one more opener.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Two openers, a close and a save, and the view-model surface.
 * D — Strong. The Application's use cases and view, and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IEntityCookieConsentCategories } from '../../application/ports/ClientVocabulary';

const START_CONSENT: TClientUseCaseInput<'startConsent'> = {};
const OPEN_PREFERENCES: TClientUseCaseInput<'openCookiePreferences'> = {};

interface IStateConsentOwn {
  readonly prefsOpen: boolean;
}

/** The first-party modal as its provider renders it: whether it is open, over the consent the session holds. */
export interface IStateConsentModal {
  readonly prefsOpen: boolean;
  readonly categories: IEntityCookieConsentCategories;
}

const CLOSED: IStateConsentOwn = { prefsOpen: false };
const OPENED: Partial<IStateConsentOwn> = { prefsOpen: true };
const SHUT: Partial<IStateConsentOwn> = { prefsOpen: false };

/**
 * THE COOKIE PREFERENCES (frontend-framework-oo P3, 2026-10-29, §28.295) — what `consent/CookieConsentProvider.tsx` kept in its state
 * and in its two module variables, the mounted provider's openers, which the legal footers' "Manage cookies" and the "Cookie
 * settings" button called (strict-ports FE4b). One per factory now: the provider is its view (its binding attaches it), and the
 * screens that open it reach it through their models or the factory. While no provider shows, the openers do nothing, as the
 * module's did; an answer that comes back to a provider gone — or to a later mount of it — opens nothing either, as the unmounted
 * component's state update did nothing.
 *
 *   - `startConsent` boots the CMP once the provider shows (idempotent; the Infrastructure adapter subscribes once);
 *   - `openPreferences` asks the certified CMP first (`openCookiePreferences` answers whether it could present its UI) and falls
 *     back to the first-party modal — also when the platform could not be asked, so "Manage cookies" always opens something;
 *   - `openModal` opens the first-party modal straight away (the "Cookie settings" button); saving is `saveCookiePreferences`.
 */
export class ConsentModalController implements IServiceViewModel<IStateConsentModal> {
  private readonly state = new ObservableState<IStateConsentOwn>(CLOSED);
  private readonly reactions = new Reactions<null>([
    { deps: () => [], run: () => { void this.useCases.startConsent(this.requests.context(), START_CONSENT); } },
  ]);
  /** The provider's mounts so far, and whether one shows now. */
  private mounts = 0;
  private showing = false;
  private shown: { readonly own: IStateConsentOwn; readonly categories: IEntityCookieConsentCategories; readonly modal: IStateConsentModal } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'startConsent' | 'openCookiePreferences' | 'saveCookiePreferences'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
  ) {}

  /** "Manage cookies": the certified CMP when it can present its UI, else the first-party modal. */
  readonly openPreferences = (): void => {
    if (!this.showing) return;
    const mount = this.mounts;
    void this.useCases.openCookiePreferences(this.requests.context(), OPEN_PREFERENCES).then((answer) => {
      if (answer.success && answer.value.opened) return;
      if (this.showing && mount === this.mounts) this.state.update(OPENED);
    });
  };

  /** "Cookie settings": the first-party modal. */
  readonly openModal = (): void => {
    if (this.showing) this.state.update(OPENED);
  };

  readonly close = (): void => this.state.update(SHUT);

  readonly save = (chosen: IEntityCookieConsentCategories): void => {
    const command: TClientUseCaseInput<'saveCookiePreferences'> = { categories: chosen };
    void this.useCases.saveCookiePreferences(this.requests.context(), command);
    this.state.update(SHUT);
  };

  /** The modal's state over the session's consent — the same object until either changes. */
  getSnapshot(): IStateConsentModal {
    const own = this.state.get();
    const categories = this.view.getSnapshot().session.consent.categories;
    if (this.shown !== null && this.shown.own === own && this.shown.categories === categories) return this.shown.modal;
    const modal: IStateConsentModal = { prefsOpen: own.prefsOpen, categories };
    this.shown = { own, categories, modal };
    return modal;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  /** The provider shows. */
  attach(): void {
    this.mounts += 1;
    this.showing = true;
  }

  /** The provider went: its openers stop, and a next mount starts closed, as a remounted component did. */
  detach(): void {
    this.showing = false;
    this.reactions.stop();
    this.state.update(SHUT);
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
